// src/app/core/services/number-format.service.ts
import { Injectable, NgZone } from '@angular/core';
import { CurrencyPipe, formatNumber as ngFormatNumber } from '@angular/common';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { UserSettingsService } from './user-settings.service';
import { UserSettingStore } from './user-setting-store.base';
import {
  applySeparators, coerceNumberFormat, NumberFormatPref, NUMBER_FORMAT_KEY, Separators, separatorsFor,
} from './number-format.util';

const DEFAULT_LOCALE = 'it';

/** App-wide cache of the user's number-format preset. A global pipe/directive can't
 *  issue an HTTP request per render, so the preset lives here in a BehaviorSubject,
 *  loaded once per authenticated transition and mutated optimistically. The cross-tab
 *  channel, auth-gated load, and optimistic write live in UserSettingStore (shared with
 *  DateFormatService). */
@Injectable({ providedIn: 'root' })
export class NumberFormatService extends UserSettingStore {
  private readonly numberFormat = new BehaviorSubject<NumberFormatPref>('system');
  // Reuse Angular's CurrencyPipe (symbol resolution + base formatting) instead of
  // re-implementing it; constructed once on the canonical it-locale base. Empty default
  // currency code mirrors the app's DEFAULT_CURRENCY_CODE='' (no symbol unless asked).
  private readonly currencyPipe = new CurrencyPipe(DEFAULT_LOCALE, '');

  readonly numberFormat$: Observable<NumberFormatPref> = this.numberFormat.asObservable();

  get currentSeparators(): Separators {
    return separatorsFor(this.numberFormat.value);
  }

  /** Synchronous snapshot of the active preset. Used by the display pipes to key their
   *  per-instance memo cache so they only re-run the (non-trivial) format work when the
   *  value, digits args, or preset actually change — not on every CD tick. */
  get currentNumberFormat(): NumberFormatPref {
    return this.numberFormat.value;
  }

  constructor(userSettings: UserSettingsService, store: Store, zone: NgZone) {
    super(userSettings, store, zone);
  }

  /** `value` when it is a finite number, else null. Single guard shared by formatNumber and
   *  formatCurrency so the two can't drift. Guards every non-finite value, not just null: a
   *  divide-by-zero derived total is NaN (ngFormatNumber renders the literal "NaN") or ±Infinity
   *  (renders "∞"/"-∞") — both print garbage in the cell, and NaN also defeats the pipe memo, so
   *  both map to null → an empty cell. NB: Number.isFinite does NOT coerce, so a numeric STRING
   *  ('12.5') is null too — unlike the old DecimalPipe/CurrencyPipe this replaced, which coerced
   *  numeric strings. Intentional and currently safe: galactus-core serializes every field rendered
   *  through these pipes as a bare JSON number (Order/OrderSummary.total, OrderPayment.toPay/total/
   *  paid, OrderDetail.price/totalPrice are Double/double; api-app JacksonSerializationCharacterization
   *  golden-snapshot proves no WRITE_NUMBERS_AS_STRINGS / Shape.STRING), so no string ever reaches
   *  here. If a future endpoint emits quoted numbers, coerce (Number(value)) BEFORE this guard —
   *  don't drop it. */
  private finiteOrNull(value: number | null | undefined): number | null {
    return value != null && Number.isFinite(value) ? value : null;
  }

  // Separators are preset-driven, not locale-driven: format on the canonical it-locale base
  // ('.' groups, ',' decimal) that applySeparators expects, then swap to the user's preset.
  // There is deliberately no locale parameter — a non-it base would scramble the swap.
  formatNumber(value: number | null | undefined, digitsInfo?: string): string | null {
    const v = this.finiteOrNull(value);
    if (v == null) return null;
    return applySeparators(ngFormatNumber(v, DEFAULT_LOCALE, digitsInfo), this.currentSeparators);
  }

  formatCurrency(
    value: number | null | undefined,
    currencyCode = '',
    display: string | boolean = 'symbol',
    digitsInfo?: string,
  ): string | null {
    const v = this.finiteOrNull(value);
    if (v == null) return null;
    const base = this.currencyPipe.transform(v, currencyCode, display, digitsInfo) ?? '';
    return applySeparators(base, this.currentSeparators);
  }

  setNumberFormat(value: NumberFormatPref): void {
    // setPref skips both the emission and the write when the preset is unchanged, so
    // re-picking the current option (or a cross-tab echo) forces no app-wide re-render of
    // every AppNumberInputDirective and no redundant HTTP write. Shared with DateFormatService.
    this.setPref(this.numberFormat, NUMBER_FORMAT_KEY, value, 'system');
  }

  protected load(userId: number): void {
    // Strict shared load keyed by userId (shared single GET with Date/ReleaseNotes; the base re-arms the
    // per-identity guard on a final failure so the next /app re-entry retries instead of pinning the
    // session on the default 'system' preset).
    this.userSettings
      .getAllSharedStrict(userId)
      .pipe(take(1))
      .subscribe({
        next: settings => this.emit(this.numberFormat, coerceNumberFormat(settings[NUMBER_FORMAT_KEY])),
        error: () => this.rearmLoad(userId),
      });
  }

  protected override handlesKey(key?: string): boolean {
    return key === NUMBER_FORMAT_KEY;
  }

  protected onBroadcast(data: { key?: string; value?: unknown }): void {
    if (data?.key === NUMBER_FORMAT_KEY) this.emit(this.numberFormat, coerceNumberFormat(data.value));
  }
}
